import { ValidatorFn, AbstractControl, ValidationErrors } from "@angular/forms"
import Decimal from 'decimal.js'

export function parseFloatSafe(n: string | number) {
  if (typeof(n) == 'string') {
    return parseFloat(n)
  }
  return n
}

export function parseIntSafe(n: string | number) {
  if (typeof(n) == 'string') {
    return parseInt(n)
  }
  return n
}

export function sortByColumn<T>(arr: T[], col: keyof T, asc = true, asNumber = false): T[] {
  return arr.sort((a, b) => {
    if (!asNumber && typeof(a[col]) == 'string' && typeof(b[col]) == 'string') {
      return (a[col] as string).localeCompare(b[col] as string) * (asc ? 1 : -1)
    }
    if (asNumber && (typeof(a[col]) == 'string' && typeof(b[col]) == 'string' || typeof(a[col]) == 'number' && typeof(b[col]) == 'number')) {
      const n1 = parseFloatSafe(a[col]), n2 = parseFloatSafe(b[col])
      if (n1 < n2) return asc ? -1 : 1
      if (n1 > n2) return asc ? 1 : -1
    }
    if (a[col] < b[col]) return asc ? -1 : 1
    if (a[col] > b[col]) return asc ? 1 : -1
    return 0
  })
}

export function sortByColumnAbs<T>(arr: T[], col: keyof T, asc = true, asNumber = false): T[] {
  return arr.sort((a, b) => {
    if (asNumber && (typeof(a[col]) == 'string' && typeof(b[col]) == 'string' || typeof(a[col]) == 'number' && typeof(b[col]) == 'number')) {
      const n1 = Math.abs(parseFloatSafe(a[col])), n2 = Math.abs(parseFloatSafe(b[col]))
      if (n1 < n2) return asc ? -1 : 1
      if (n1 > n2) return asc ? 1 : -1
    }
    if (typeof(a[col]) == 'number' && typeof(b[col]) == 'number') {
      if (Math.abs(a[col]) < Math.abs(b[col])) return asc ? -1 : 1
      if (Math.abs(a[col]) > Math.abs(b[col])) return asc ? 1 : -1
    }
    return 0
  })
}

export function keyboardLayoutSwitch(russianText: string): string {
  const layoutMap: Record<string, string> = {
    'й': 'q', 'ц': 'w', 'у': 'e', 'к': 'r', 'е': 't', 'н': 'y', 'г': 'u', 'ш': 'i', 'щ': 'o', 'з': 'p',
    'ф': 'a', 'ы': 's', 'в': 'd', 'а': 'f', 'п': 'g', 'р': 'h', 'о': 'j', 'л': 'k', 'д': 'l',
    'я': 'z', 'ч': 'x', 'с': 'c', 'м': 'v', 'и': 'b', 'т': 'n', 'ь': 'm', 'б': ',', 'ю': '.',
    'Й': 'Q', 'Ц': 'W', 'У': 'E', 'К': 'R', 'Е': 'T', 'Н': 'Y', 'Г': 'U', 'Ш': 'I', 'Щ': 'O', 'З': 'P',
    'Ф': 'A', 'Ы': 'S', 'В': 'D', 'А': 'F', 'П': 'G', 'Р': 'H', 'О': 'J', 'Л': 'K', 'Д': 'L',
    'Я': 'Z', 'Ч': 'X', 'С': 'C', 'М': 'V', 'И': 'B', 'Т': 'N', 'Ь': 'M', 'Б': '<', 'Ю': '>'
  }
  return russianText.split('').map(char => layoutMap[char] || char).join('');
}

export type RoundingMode = 'round' | 'floor' | 'ceil'

export function roundToStep(value: number | string, step: number | string, mode: RoundingMode = 'round'): number {
  const val = new Decimal(value)
  switch (mode) {
    case 'floor':
      return val.toNearest(step, Decimal.ROUND_DOWN).toNumber()
    case 'ceil':
      return val.toNearest(step, Decimal.ROUND_UP).toNumber()
    case 'round':
    default:
      return val.toNearest(step).toNumber()
  }
}

export function stepValidator(step: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value

    if (value == null || value === '' || !step) {
      return null; // не проверяем пустые значения (оставляем для required)
    }

    const numericValue = parseFloatSafe(value)

    if (isNaN(numericValue)) {
      return { step: { requiredStep: step, actual: value, reason: 'NaN' } }
    }

    const roundedValue = roundToStep(numericValue, step)

    if (Math.abs(numericValue - roundedValue) > 0.000000001) {
      return { step: { requiredStep: step, actual: numericValue } }
    }

    return null; // всё ок
  }
}

type StringKeys<T> = {
  [K in keyof T]: T[K] extends string ? K : never
}[keyof T]

type NumberLikeKeys<T> = {
  [K in keyof T]: T[K] extends string | number ? K : never
}[keyof T]

export function createStringNumberMap<T>(arrayOfObjects: T[], keyProperty: StringKeys<T>, valueProperty: NumberLikeKeys<T>) {
  const map: Record<string, number> = {}
  arrayOfObjects.forEach(obj => {
    const key = obj[keyProperty] as string, value = obj[valueProperty] as string | number
    map[key] = (map[key] ?? 0) + parseFloatSafe(value)
  })
  return map
}

export function mergeStringNumberMaps(obj1: Record<string, number>, obj2: Record<string, number>) {
  const merged: Record<string, number> = {...obj1}
  for (const [key, value] of Object.entries(obj2)) {
    merged[key] = (merged[key] ?? 0) + value
  }
  return merged
}

export function filterStringNumberMap(obj: Record<string, number>, filterFn: (key: string, value: number) => boolean) {
  const res: Record<string, number> = {}
  Object.entries(obj).forEach(([key, value]) => {
    if (filterFn(key, value)) {
      res[key] = value
    }
  })
  return res
}

export function sortStringNumberMap(obj: Record<string, number>, asc = true): Record<string, number> {
  let arr = Object.entries(obj)
  arr = sortByColumn(arr, 1, asc, true)
  return Object.fromEntries(arr)
}
